'use client';

import React from 'react';
import {
  Captions,
  Cloud,
  Download,
  Edit,
  Eye,
  FileText,
  Film,
  Globe,
  Headphones,
  Languages,
  LucideIcon,
  Mic2,
  MicOff,
  Monitor,
  PenTool,
  Sliders,
  SlidersHorizontal,
  UserCircle,
  Users,
  VoicemailIcon,
  Volume2,
  Zap
} from 'lucide-react';

import type { FeatureItem } from '~/types/feature-type';
import {
  AutoSyncMockup,
  BrowserRecordingMockup,
  CaptionsMockup,
  CollaborationMockup,
  EducationVoiceMockup,
  ExportMockup,
  LessonTimelineMockup,
  NoMicrophoneMockup,
  ScriptCollaborationMockup,
  ScriptEditorMockup,
  ScriptWritingMockup,
  TimelineEditorMockup,
  TranslationMockup,
  VisualAnalysisMockup,
  VoiceCloningMockup,
  VoiceGridMockup,
  VoiceLibraryMockup
} from '../mockups/features-mockups';

const iconMap: Record<string, LucideIcon> = {
  penTool: PenTool,
  globe: Globe,
  userCircle: UserCircle,
  slidersHorizontal: SlidersHorizontal,
  zap: Zap,
  captions: Captions,
  film: Film,
  fileText: FileText,
  mic2: Mic2,
  micOff: MicOff,
  sliders: Sliders,
  users: Users,
  voicemailIcon: VoicemailIcon,
  download: Download,
  volume2: Volume2,
  eye: Eye,
  edit: Edit,
  languages: Languages,
  cloud: Cloud,
  monitor: Monitor,
  headphones: Headphones
};

const getMockupComponent = (type?: string) => {
  switch (type) {
    case 'script':
      return <ScriptWritingMockup />;
    case 'voices':
      return <VoiceLibraryMockup />;
    case 'voiceClone':
      return <VoiceCloningMockup />;
    case 'timeline':
      return <TimelineEditorMockup />;
    case 'autoSync':
      return <AutoSyncMockup />;
    case 'captions':
      return <CaptionsMockup />;
    case 'export':
      return <ExportMockup />;
    case 'voiceGrid':
      return <VoiceGridMockup />;
    case 'collaboration':
      return <CollaborationMockup />;
    case 'translation':
      return <TranslationMockup />;
    case 'visualAnalysis':
      return <VisualAnalysisMockup />;
    case 'scriptEditor':
      return <ScriptEditorMockup />;
    case 'scriptCollaboration':
      return <ScriptCollaborationMockup />;
    case 'educationVoice':
      return <EducationVoiceMockup />;
    case 'lessonTimeline':
      return <LessonTimelineMockup />;
    case 'noMicrophone':
      return <NoMicrophoneMockup />;
    case 'browserRecording':
      return <BrowserRecordingMockup />;
    default:
      return null;
  }
};

type FeatureCardProps = {
  feature: FeatureItem;
};

export const FeatureCard: React.FC<FeatureCardProps> = ({ feature }) => {
  const IconComponent = iconMap[feature.icon];
  return (
    <div className="h-full bg-card border border-border rounded-2xl p-6 hover:border-primary/50 hover:shadow-lg transition-all duration-300 group flex flex-col">
      {/* Mockup - fixed height for consistency */}
      {feature.mockupType && (
        <div className="mb-5 rounded-xl overflow-hidden h-[280px]">
          <div className="h-full [&>div]:h-full [&>div]:flex [&>div]:flex-col">
            {getMockupComponent(feature.mockupType)}
          </div>
        </div>
      )}

      {/* Title with icon */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
          {IconComponent && <IconComponent className="w-5 h-5 text-primary" />}
        </div>
        <h3 className="text-xl font-bold text-foreground leading-snug">
          {feature.title}
        </h3>
      </div>

      {/* Description - larger font for readability */}
      <p className="text-muted-foreground text-base leading-relaxed mb-5 flex-grow">
        {feature.description}
      </p>

      {/* Tags - smaller light grey badges */}
      <div className="flex flex-wrap gap-1.5 mt-auto">
        {feature.tags.map((tag, i) => (
          <span
            key={i}
            className="px-2.5 py-1 bg-muted text-foreground/70 text-xs font-medium rounded-full"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
};

export default FeatureCard;
